import { GetTasksDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  BulkCreateTaskDocument,
  CreateProjectTaskDocument,
  CreateTaskDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import TaskForm, { ModalTaskWithFiles } from 'modules/tasks/components/TaskForm'
import { useCreateTaskFile } from 'modules/tasks/hooks/useCreateTaskFile'
import { defaultStatus } from 'modules/tasks/lib/status'
import tasksFiles, { TasksFiles } from 'modules/tasks/store/collections/tasks_files'
import type TaskFile from 'modules/tasks/store/models/TaskFile'

interface Props {
  currentUserId: number
  onClose: () => void
}

const NewTaskModal = ({ onClose }: Props) => {
  const { createTaskFile, loading: creatingTaskFile } = useCreateTaskFile()
  const { employee, access } = useCurrentSession()

  const { id, firstName, lastName } = employee || {}

  const [createTask, { loading: creatingTask }] = useMutation(CreateTaskDocument, {
    extractorFn: (data) => data.tasksMutations.createTasksTask,
    refetchQueries: [GetTasksDataTableDocument],
  })

  const [createTaskPerAssignee, { loading: creatingTaskPerAssignee }] = useMutation(
    BulkCreateTaskDocument,
    {
      extractorFn: (data) => data.tasksMutations.bulkCreateTasksTask,
      refetchQueries: [GetTasksDataTableDocument],
    }
  )

  const [createProjectTask, { loading: creatingProjectTask }] = useMutation(
    CreateProjectTaskDocument,
    {
      extractorFn: (mutationData) =>
        mutationData.projectManagementMutations.createProjectmanagementProjectTask,
    }
  )

  const saveFileWithTask = async (file: TaskFile, taskId: number) => {
    const result = await createTaskFile({
      taskId,
      file: file.attributes.file,
    })
    handleResult(result, (errors) => errors && form.setErrors(errors))
  }

  // The main function to save all files with multiple taskIds in parallel:
  const saveFilesWithTaskIds = async (newFiles: TaskFile[], taskIds: number[]) => {
    const promises: Promise<void>[] = []

    for (const file of newFiles) {
      for (const taskId of taskIds) {
        promises.push(saveFileWithTask(file, taskId))
      }
    }

    await Promise.all(promises)
  }

  // Default employee selection for the current user
  const defaultEmployeeSelection: EmployeeSelectorV2MultiSelectState = {
    type: 'manual',
    ids: access?.id ? [access.id] : [],
  }

  const form = useForm<ModalTaskWithFiles>({
    defaultValues: {
      name: '',
      content: '',
      assignees: {
        nodes: [
          {
            employeeName: {
              id: id || 0,
              fullName: `${firstName} ${lastName}`,
              firstName: firstName || '',
              lastName: lastName || '',
              access: access,
            },
          },
        ],
      },
      id: -1,
      dueOn: '',
      newFiles: new TasksFiles(),
      removeFiles: new TasksFiles(),
      employeeSelection: defaultEmployeeSelection,
    },
    onSubmit: async (data, { setErrors }) => {
      const creationData = {
        content: data.content || '',
        dueOn: data.dueOn || null,
        name: data.name,
        employeeSelection: data.employeeSelection,
      }
      const taskIds: number[] = []
      let handled

      // Create a task for each employee assigned to the task if user checks createTaskPerAsignee checkbox
      if (data.createTaskPerAsignee) {
        const result = await createTaskPerAssignee({
          variables: creationData,
        })
        handled = handleResult(result, (errors) => errors && setErrors(errors))
        handleResponse(handled)

        handled.success &&
          handled.data.tasks &&
          handled.data.tasks.forEach(({ id }) => taskIds.push(id))
      } else if (data.projectId) {
        // For project tasks, we still need to pass assigneeEmployeeIds (different mutation)
        const result = await createProjectTask({
          variables: {
            content: data.content || '',
            dueOn: data.dueOn || null,
            name: data.name,
            assigneeEmployeeIds: data.assignees.nodes
              .map((assignee) => assignee.employeeName?.id)
              .filter(Boolean),
            projectId: data.projectId,
            subprojectId: data.subprojectId,
            status: defaultStatus().status,
          },
        })
        handled = handleResult(result, (errors) => errors && setErrors(errors))
        handleResponse(handled)
        handled.success &&
          handled.data.projectTask?.task &&
          taskIds.push(handled.data.projectTask.task.id)
      } else {
        const result = await createTask({ variables: creationData })
        handled = handleResult(result, (errors) => errors && setErrors(errors))
        handleResponse(handled)

        handled.success && handled.data.task && taskIds.push(handled.data.task.id)
      }

      const newFiles = data.newFiles
      await saveFilesWithTaskIds(newFiles.toArray(), taskIds)
      tasksFiles.add(newFiles.toArray())

      handled && handled.success && onClose()
      return handled! // eslint-disable-line no-type-assertion/no-type-assertion
    },
  })

  const handleResponse = (result: Result<unknown>) => {
    if (result.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('common.save_error') })
    }
  }

  if (!employee) {
    return null
  }

  return (
    <TaskForm
      mode="new"
      form={form}
      submitting={
        creatingTask || creatingTaskPerAssignee || creatingTaskFile || creatingProjectTask
      }
      close={onClose}
      onSubmit={async (removeFiles, newFiles) => {
        form.setFieldValue('removeFiles', removeFiles)
        form.setFieldValue('newFiles', newFiles)
        form.handleSubmit()
      }}
      files={[]}
    />
  )
}

export default NewTaskModal
